"use client";

import { ArrowRight, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * SellHero — the "Start selling for free." promo banner at the top of the feed
 * landing (mockup: Kraahl Desktop Home). Dark surface, display headline, a foil
 * "Sell Now" primary (the one possible action) + a glass "How it works". Reuses
 * the DA foil button variant. Copy per the design-system voice (readme.md).
 */
export function SellHero({
  onSellAction,
  onHowItWorksAction,
  className,
}: {
  onSellAction?: () => void;
  onHowItWorksAction?: () => void;
  className?: string;
}) {
  return (
    <section
      className={cn(
        // `.sell-hero-stage` (globals.css) is the theme-aware surface: foil
        // glows + text-legibility ink gradient + base, with a `.dark` override
        // — replaces the old hardcoded bg-neutral-900 + two overlay divs that
        // made the banner a black slab on the light theme.
        "sell-hero-stage relative overflow-hidden rounded-[26px] border border-black/10 dark:border-white/10",
        "h-[220px] sm:h-[260px] lg:h-[280px]",
        className,
      )}
    >
      <div className="absolute inset-y-0 left-0 right-0 flex max-w-[560px] flex-col justify-center px-7 text-foreground sm:px-10 lg:px-12">
        <h2 className="font-display text-4xl font-bold leading-[0.98] tracking-[-0.035em] sm:text-5xl">
          Start selling
          <br />
          for free.
        </h2>
        <p className="mt-3 max-w-[430px] text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
          Keep 100% of every sale. Zero fees, and instant payouts through PayPal.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3 sm:mt-6">
          <Button variant="foil" size="lg" onClick={onSellAction}>
            <Plus className="mr-2 size-4" />
            Sell Now
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={onHowItWorksAction}
            className="hidden border border-black/20 bg-black/5 text-foreground backdrop-blur-sm hover:bg-black/10 sm:flex dark:border-white/40 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
          >
            How it works
            <ArrowRight className="ml-2 size-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
